import { BasicModal, DefaultButton, PageContentPreloader } from 'common/components';
import { ReduxSelectField, ReduxTextField } from 'common/components/form-fields';
import { FormMessageHOC } from 'common/HOCs';
import { withTheme } from 'common/styling/theme';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { change, Field, formValueSelector, reduxForm, untouch } from 'redux-form';
import { numericality, required } from 'redux-form-validators';
import { LeftRightArrow } from 'common/icons';
import {
  createInnerTransfer,
  getInnerTransferInfo,
  getInnerTransferOptions,
  getMultiWalletTransferOptions,
} from '../../_redux';
import InternalTransferConfirmation from '../InternalTransferConfirmation';
import TransferWarning from '../TransferWarning';

import { getDynamicStyles, staticStyles } from './style';

const selector = formValueSelector('inner-transfer-form');
const getFixedNumber = (num, decimal) => Number(Number(num).toFixed(decimal));
const getWalletAccountOptionValue = id => `walletAccount:${id}`;
const isWalletAccountOption = value => typeof value === 'string' && value.startsWith('walletAccount:');
const getWalletAccountId = value => {
  const id = value.replace('walletAccount:', '');
  const numericId = Number(id);

  return Number.isNaN(numericId) ? id : numericId;
};
const getTransferAccountValue = value => (isWalletAccountOption(value) ? getWalletAccountId(value) : value);
const getWalletAccountOperationType = ({ senderAccount, recipientAccount }) => {
  const senderIsWalletAccount = isWalletAccountOption(senderAccount);
  const recipientIsWalletAccount = isWalletAccountOption(recipientAccount);
  const senderIsAccount = senderAccount !== 'wallet' && !senderIsWalletAccount;
  const recipientIsAccount = recipientAccount !== 'wallet' && !recipientIsWalletAccount;

  if (senderIsWalletAccount && recipientIsAccount) {
    return 'deposit';
  }

  if (senderIsAccount && recipientIsWalletAccount) {
    return 'withdraw';
  }

  return '';
};
const getCommissionMessageId = ({ senderAccount, recipientAccount }, isSameCurrency) => {
  const currenciesType = isSameCurrency ? 'SameCurrencies' : 'DifferentCurrencies';

  if (senderAccount === 'wallet') {
    return `commissionForInternalTransferFromWallet${currenciesType}`;
  }

  if (recipientAccount === 'wallet') {
    return `commissionForInternalTransferToWallet${currenciesType}`;
  }

  if (isWalletAccountOption(senderAccount) && !isWalletAccountOption(recipientAccount)) {
    return `commissionForInternalTransferFromWalletAccount${currenciesType}`;
  }

  if (!isWalletAccountOption(senderAccount) && isWalletAccountOption(recipientAccount)) {
    return `commissionForInternalTransferToWalletAccount${currenciesType}`;
  }

  return `commissionForInternalTransfer${currenciesType}`;
};
const getInnerTransferPayload = values => {
  const operationType = getWalletAccountOperationType(values);
  const payload = {
    ...values,
    senderAccount: getTransferAccountValue(values.senderAccount),
    recipientAccount: getTransferAccountValue(values.recipientAccount),
  };

  if (operationType) {
    return {
      ...payload,
      isMultiWalletTransfer: true,
      operationType,
    };
  }

  return payload;
};

const InnerTransfer = ({
  formValues,
  innerTransferOptions,
  innerTransferInfo,
  innerTransferInfoIsLoaded,
  innerTransferOptionsAreLoaded,
  walletTransferOptions,
  walletTransferOptionsAreLoaded,
  getInnerTransferOptions,
  getWalletTransferOptions,
  getInnerTransferInfo,
  allowNegativeBalanceTransfer,
  profile,
  addons,
  isWalletDepositEnabled,
  isWalletWithdrawEnabled,
  isWalletAccountDepositEnabled,
  isWalletAccountWithdrawEnabled,
  handleSubmit,
  pristine,
  submitting,
  invalid,
  transfersError,
  error,
  dispatch,
  intl,
  theme,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReceiveAmountNegative, setIsReceiveAmountNegative] = useState(false);
  const [sendCurrency, setSendCurrency] = useState('');
  const [receiveCurrency, setReceiveCurrency] = useState('');
  const [isSendAmountDisabled, setSendAmountDisabled] = useState(true);
  const submit = handleSubmit(values => createInnerTransfer(getInnerTransferPayload(values), dispatch));

  const { senderCurrencyDigit = 2, recipientCurrencyDigit = 2 } = useMemo(() => {
    if (innerTransferInfo.details) {
      const { senderCurrencyDigit, recipientCurrencyDigit } = innerTransferInfo.details;
      return { senderCurrencyDigit, recipientCurrencyDigit };
    }
    return {};
  }, [innerTransferInfo.details]);

  const min = innerTransferInfo.minTransfer || 0;
  const maxTransfer = innerTransferInfo.maxTransfer || 0;
  const availableAmount = innerTransferInfo.availableAmount || 0;
  const max = maxTransfer > availableAmount ? availableAmount : maxTransfer;

  const minMessage = intl.formatMessage({ id: 'clientInnerTransferErrorMin' }, { min, currency: sendCurrency });
  const maxMessage = intl.formatMessage({ id: 'clientInnerTransferErrorMax' }, { max, currency: sendCurrency });
  const insufficientFundsMessage = intl.formatMessage({ id: 'clientInnerTransferErrorInsufficientFunds' });
  const isWalletEnabled = addons.includes('wallet');
  const isWalletAccountsEnabled = addons.includes('walletAccounts');
  const isSenderWalletEnabled = isWalletEnabled && isWalletDepositEnabled;
  const isRecipientWalletEnabled = isWalletEnabled && isWalletWithdrawEnabled;
  const isSenderWalletAccountEnabled = isWalletAccountsEnabled && isWalletAccountDepositEnabled;
  const isRecipientWalletAccountEnabled = isWalletAccountsEnabled && isWalletAccountWithdrawEnabled;
  const shouldLoadWalletTransferOptions = isSenderWalletAccountEnabled || isRecipientWalletAccountEnabled;
  const isWalletTransferOptionsLoaded = !shouldLoadWalletTransferOptions || walletTransferOptionsAreLoaded;
  const isTransferOptionsLoaded = innerTransferOptionsAreLoaded && isWalletTransferOptionsLoaded;

  const walletAccount = useMemo(
    () => ({
      id: 'wallet',
      accountType: intl.formatMessage({ id: 'menuWallet' }),
      login: '',
      balance: {
        amount: profile.walletBalance,
        currency: 'USD',
      },
    }),
    [intl, profile.walletBalance]
  );

  const walletAccountOptions = walletTransferOptions.map(item => ({
    ...item,
    id: getWalletAccountOptionValue(item.id),
  }));

  const senderAccounts = [
    ...(isSenderWalletEnabled ? [walletAccount] : []),
    ...(isSenderWalletAccountEnabled ? walletAccountOptions : []),
    ...innerTransferOptions,
  ].map(item => ({
    label: `${item.accountType}${item.login ? ` ${item.login}` : ''}${
      item.balance ? ` - ${item.balance.amount}${item.balance.currency}` : ''
    }`,
    value: item.id,
  }));

  const recipientAccounts = [
    ...(isRecipientWalletEnabled ? [walletAccount] : []),
    ...(isRecipientWalletAccountEnabled ? walletAccountOptions : []),
    ...innerTransferOptions,
  ].map(item => ({
    label: `${item.accountType}${item.login ? ` ${item.login}` : ''}${
      item.balance ? ` - ${item.balance.amount}${item.balance.currency}` : ''
    }`,
    value: item.id,
  }));

  const getAccountInfo = account => {
    if (account === 'wallet') {
      return walletAccount;
    }

    if (isWalletAccountOption(account)) {
      const walletAccountId = getWalletAccountId(account);
      return walletTransferOptions.find(item => String(item.id) === String(walletAccountId));
    }

    return innerTransferOptions.find(item => item.id === account);
  };

  useEffect(() => {
    getInnerTransferOptions();
  }, [getInnerTransferOptions]);

  useEffect(() => {
    if (shouldLoadWalletTransferOptions) {
      getWalletTransferOptions();
    }
  }, [getWalletTransferOptions, shouldLoadWalletTransferOptions]);

  const toggleTransferConfirmationModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const resetTransferAmounts = () => {
    dispatch(change('inner-transfer-form', 'sendAmount', null));
    dispatch(untouch('inner-transfer-form', 'sendAmount'));
    dispatch(change('inner-transfer-form', 'receiveAmount', null));
  };

  const getInnerTransferCommission = (...values) => {
    const { senderAccount, recipientAccount } = formValues;
    if (values[3] === 'senderAccount' && recipientAccount) {
      resetTransferAmounts();
      if (values[0] !== recipientAccount) {
        getInnerTransferInfo({
          ...getInnerTransferPayload({
            senderAccount: values[0],
            recipientAccount,
          }),
        });
        setSendCurrency(getAccountInfo(values[0]).balance?.currency ?? 'USD');

        setReceiveCurrency(getAccountInfo(recipientAccount).balance?.currency ?? 'USD');
        setSendAmountDisabled(false);
      } else {
        dispatch(change('inner-transfer-form', 'recipientAccount', null));
        dispatch(untouch('inner-transfer-form', 'recipientAccount'));
        setSendAmountDisabled(true);
      }
    } else if (values[3] === 'recipientAccount' && senderAccount) {
      resetTransferAmounts();
      if (values[0] !== senderAccount) {
        getInnerTransferInfo({
          ...getInnerTransferPayload({
            recipientAccount: values[0],
            senderAccount,
          }),
        });
        setSendCurrency(getAccountInfo(senderAccount).balance?.currency ?? 'USD');
        setReceiveCurrency(getAccountInfo(values[0]).balance?.currency ?? 'USD');
        setSendAmountDisabled(false);
      } else {
        dispatch(change('inner-transfer-form', 'senderAccount', null));
        dispatch(untouch('inner-transfer-form', 'senderAccount'));
        setSendAmountDisabled(true);
      }
    }
  };

  const calculateReceiveAmount = (_, value) => {
    const sendAmount = parseFloat(value);
    if (
      typeof sendAmount === 'number' &&
      sendAmount <= innerTransferInfo.availableAmount &&
      innerTransferInfo.details
    ) {
      const { exchangeCoefficient = 1, commission } = innerTransferInfo.details;
      let calculatedCommission =
        (sendAmount / 100) * commission.percentage +
        commission.fix * innerTransferInfo.details.exchangeCoefficientForFix;
      if (calculatedCommission > innerTransferInfo.details.maxCommission.amount)
        calculatedCommission = innerTransferInfo.details.maxCommission.amount;
      if (calculatedCommission < innerTransferInfo.details.minCommission.amount)
        calculatedCommission = innerTransferInfo.details.minCommission.amount;
      const receiveAmount = (sendAmount - calculatedCommission) * exchangeCoefficient;
      let roundedReceiveAmount = 0;
      if (receiveAmount > 0) {
        roundedReceiveAmount = getFixedNumber(receiveAmount, recipientCurrencyDigit);
        setIsReceiveAmountNegative(false);
      } else {
        setIsReceiveAmountNegative(true);
      }
      dispatch(change('inner-transfer-form', 'receiveAmount', roundedReceiveAmount.toString()));
    }
  };

  const checkForDigitsAfterDecimal = useCallback(
    value => {
      const regex = new RegExp(`^\\d+\\.?\\d{0,${senderCurrencyDigit}}$`);
      if (!regex.test(value)) {
        return intl.formatMessage(
          { id: 'maximumNumberOfDigitsAfterDecimalPoint' },
          { maximumDigits: senderCurrencyDigit }
        );
      }
      return null;
    },
    [senderCurrencyDigit]
  );

  const recpieintAccountInfo = getAccountInfo(formValues.recipientAccount);
  const recipientAccountInfoBalance = useMemo(
    () => Number(recpieintAccountInfo?.balance?.amount) || 0,
    [recpieintAccountInfo]
  );
  const canSwapAccounts =
    formValues.senderAccount &&
    formValues.recipientAccount &&
    formValues.senderAccount !== formValues.recipientAccount &&
    senderAccounts.some(({ value }) => value === formValues.recipientAccount) &&
    recipientAccounts.some(({ value }) => value === formValues.senderAccount);

  const isNegativeDisclaimerVisible = useMemo(() => {
    if (allowNegativeBalanceTransfer) {
      return false;
    }

    return recipientAccountInfoBalance < 0;
  }, [recipientAccountInfoBalance]);

  const handleSwapAccounts = () => {
    const { senderAccount, recipientAccount } = formValues;

    if (!canSwapAccounts) {
      return;
    }

    dispatch(change('inner-transfer-form', 'senderAccount', recipientAccount));
    dispatch(change('inner-transfer-form', 'recipientAccount', senderAccount));
    resetTransferAmounts();
    getInnerTransferInfo({
      ...getInnerTransferPayload({
        senderAccount: recipientAccount,
        recipientAccount: senderAccount,
      }),
    });
    setSendCurrency(getAccountInfo(recipientAccount).balance?.currency ?? 'USD');
    setReceiveCurrency(getAccountInfo(senderAccount).balance?.currency ?? 'USD');
    setSendAmountDisabled(false);
  };

  return (
    <PageContentPreloader ready={isTransferOptionsLoaded} type="bigLine">
      <form className="InnerTransfer" onSubmit={submit}>
        <div className="InnerTransfer__line InnerTransfer__accounts-line">
          <Field
            component={ReduxSelectField}
            name="senderAccount"
            textId="justFrom"
            options={senderAccounts}
            onChange={getInnerTransferCommission}
            validate={[required()]}
          />
          <button
            className="InnerTransfer__swap-button"
            type="button"
            onClick={handleSwapAccounts}
            disabled={!canSwapAccounts}>
            <LeftRightArrow />
          </button>
          <Field
            component={ReduxSelectField}
            name="recipientAccount"
            textId="justTo"
            options={recipientAccounts}
            onChange={getInnerTransferCommission}
            validate={[required()]}
          />
        </div>
        <div className="InnerTransfer__line">
          <Field
            component={ReduxTextField}
            type="text"
            name="sendAmount"
            textId="clientSendAmount"
            validate={[
              required(),
              checkForDigitsAfterDecimal,
              numericality({
                '>=': min,
                '<=': max,
                msg: {
                  '>=': minMessage,
                  '<=': max === availableAmount ? insufficientFundsMessage : maxMessage,
                },
              }),
            ]}
            disabled={submitting || !innerTransferInfoIsLoaded || isSendAmountDisabled}
            onChange={calculateReceiveAmount}
            description={sendCurrency}
            shouldUseOnlyFormValue
          />
          <Field
            component={ReduxTextField}
            type="text"
            name="receiveAmount"
            textId="justWillReceive"
            validate={[required()]}
            description={receiveCurrency}
            disabled
          />
        </div>
        {transfersError && <span className="InnerTransfer__error">{transfersError}</span>}
        {innerTransferInfo.details && (
          <div className="InnerTransfer__description">
            <FormattedMessage
              id={getCommissionMessageId(formValues, sendCurrency === receiveCurrency)}
              values={{
                commission: `${innerTransferInfo.details.commission.percentage}% + ${innerTransferInfo.details.commission.fix} USD.`,
              }}>
              {txt => <span>{txt}</span>}
            </FormattedMessage>
            {innerTransferInfo.details.exchangeRate && <span>{innerTransferInfo.details.exchangeRate}</span>}
            {innerTransferInfo.details.minCommission && innerTransferInfo.details.maxCommission && (
              <FormattedMessage
                id="commissionLimitDescription"
                values={{
                  min: `${innerTransferInfo.details.minCommission.amount} ${innerTransferInfo.details.minCommission.currency}`,
                  max: `${innerTransferInfo.details.maxCommission.amount} ${innerTransferInfo.details.maxCommission.currency}`,
                }}>
                {txt => <span>{txt}</span>}
              </FormattedMessage>
            )}
          </div>
        )}

        {!isNegativeDisclaimerVisible && (
          <DefaultButton
            textId="clientSend"
            onClick={toggleTransferConfirmationModal}
            type="button"
            disabled={isReceiveAmountNegative || invalid || pristine || submitting}
            filled
          />
        )}

        {isNegativeDisclaimerVisible && <TransferWarning warningTextId="clientTransferNegativeAmountWarning" />}
      </form>

      <BasicModal
        isOpen={isModalOpen}
        captionId="internalTransferConfirmation"
        onRequestClose={toggleTransferConfirmationModal}>
        <InternalTransferConfirmation
          payload={{
            senderAccount: getAccountInfo(formValues.senderAccount),
            recipientAccount: getAccountInfo(formValues.recipientAccount),
            sendAmount: `${formValues.sendAmount} ${sendCurrency}`,
            receiveAmount: `${formValues.receiveAmount} ${receiveCurrency}`,
          }}
          submitting={submitting}
          handleSubmit={submit}
          handleCancel={toggleTransferConfirmationModal}
          error={error}
        />
      </BasicModal>

      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </PageContentPreloader>
  );
};

InnerTransfer.propTypes = {
  formValues: PropTypes.object.isRequired,
  innerTransferInfo: PropTypes.object.isRequired,
  innerTransferInfoIsLoaded: PropTypes.bool.isRequired,
  innerTransferOptionsAreLoaded: PropTypes.bool.isRequired,
  walletTransferOptionsAreLoaded: PropTypes.bool.isRequired,
  getInnerTransferOptions: PropTypes.func.isRequired,
  getWalletTransferOptions: PropTypes.func.isRequired,
  getInnerTransferInfo: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  transfersError: PropTypes.string.isRequired,
  error: PropTypes.string,
  intl: PropTypes.object.isRequired,
  invalid: PropTypes.bool.isRequired,
  innerTransferOptions: PropTypes.array,
  walletTransferOptions: PropTypes.array,
  allowNegativeBalanceTransfer: PropTypes.bool,
  profile: PropTypes.object,
  addons: PropTypes.array,
  isWalletDepositEnabled: PropTypes.bool,
  isWalletWithdrawEnabled: PropTypes.bool,
  isWalletAccountDepositEnabled: PropTypes.bool,
  isWalletAccountWithdrawEnabled: PropTypes.bool,
  theme: PropTypes.object,
};

InnerTransfer.defaultProps = {
  allowNegativeBalanceTransfer: true,
  innerTransferOptions: [],
  walletTransferOptions: [],
  profile: {},
  addons: [],
  isWalletDepositEnabled: false,
  isWalletWithdrawEnabled: false,
  isWalletAccountDepositEnabled: false,
  isWalletAccountWithdrawEnabled: false,
  error: '',
  theme: '',
};

export default compose(
  withTheme(),
  withRouter,
  injectIntl,
  FormMessageHOC,
  connect(
    state => ({
      formValues: selector(state, 'senderAccount', 'recipientAccount', 'sendAmount', 'receiveAmount'),
      innerTransferOptions: state.client.transfers.innerTransferOptions,
      innerTransferOptionsAreLoaded: state.client.transfers.innerTransferOptionsAreLoaded,
      walletTransferOptions: state.client.transfers.multiWalletTransferOptions,
      walletTransferOptionsAreLoaded: state.client.transfers.multiWalletTransferOptionsAreLoaded,
      innerTransferInfo: state.client.transfers.innerTransferInfo,
      innerTransferInfoIsLoaded: state.client.transfers.innerTransferInfoIsLoaded,
      transfersError: state.client.transfers.error,
      allowNegativeBalanceTransfer: state.interfaceConfig.allowNegativeBalanceTransfer,
      profile: state.user.profile,
      addons: state.interfaceConfig.addons,
      isWalletDepositEnabled:
        state.interfaceConfig.internalTransfers.from_wallet_enabled ||
        state.interfaceConfig.internalTransfers.from_wallet_account_enabled,
      isWalletWithdrawEnabled:
        state.interfaceConfig.internalTransfers.to_wallet_enabled ||
        state.interfaceConfig.internalTransfers.to_wallet_account_enabled,
      isWalletAccountDepositEnabled: state.interfaceConfig.internalTransfers.from_wallet_account_enabled,
      isWalletAccountWithdrawEnabled: state.interfaceConfig.internalTransfers.to_wallet_account_enabled,
    }),
    {
      getInnerTransferOptions: () => getInnerTransferOptions.request(),
      getWalletTransferOptions: () => getMultiWalletTransferOptions.request(),
      getInnerTransferInfo: data => getInnerTransferInfo.request(data),
    }
  ),
  reduxForm({
    form: 'inner-transfer-form',
    onSubmitSuccess(result, dispatch, props) {
      props.showFormSuccessMessage(props.successMessageTextId);
      props.history.push('/payments?tab=transfers');
    },
  })
)(InnerTransfer);
export { InnerTransfer };
